<template>
  <v-select
    v-model="deliveryMethod"
    data-cy="delivery-method-select"
    :items="effectiveDeliveryMethods"
    :placeholder="t('donationGuide.payment.selectShippingMethodLabel')"
    :loading="loading"
    :rules="[rules.requiredSelection(t('donationDetails.form.deliveryMethod.label'))]"
    item-value="id"
    item-title="title"
    hide-details="auto"
    required
    return-object
    density="comfortable"
    :menu-props="{ maxWidth: 400 }"
    @change="changed"
  >
    <template #prepend-inner>
      <v-icon v-if="deliveryMethod" :color="deliveryMethod.color">
        {{ deliveryMethod.icon }}
      </v-icon>
      <v-icon v-else color="grey" style="margin-right: 8px"> mdi-arrow-right </v-icon>
    </template>
    <template #selection="{ item }">
      <span style="margin-left: -8px">{{ t(item.raw.nameTranslationKey) }}</span>
    </template>
    <template #item="{ item, props }">
      <v-list-item v-bind="props">
        <template #prepend>
          <v-icon :color="item.raw.color">
            {{ item.raw.icon }}
          </v-icon>
        </template>
        <v-tooltip bottom open-delay="500">
          <template #activator="{ activatorProps }">
            <v-list-item-subtitle v-bind="activatorProps">
              <div class="grey--text multilined-item">
                {{ t(item.raw.descriptionTranslationKey) }}
              </div>
            </v-list-item-subtitle>
          </template>
          <span>{{ t(item.raw.descriptionTranslationKey) }}</span>
        </v-tooltip>
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useLabel } from '@/composables/useLabel';
import { DeliveryMethod } from '@/classes/DeliveryMethod';
import { useValidationRules } from '@/composables/useValidationRules';

export default defineComponent({
  props: {
    modelValue: {
      type: Object as () => DeliveryMethod,
      default: null,
    },
    deliveryMethods: {
      type: Array as () => Array<DeliveryMethod>,
      default: () => [],
    },
    loading: Boolean,
  },
  emits: ['update:modelValue', 'change'],
  setup(componentProps, { emit }) {
    const deliveryMethod = computed({
      get: function () {
        return componentProps.modelValue;
      },
      set: function (newValue) {
        emit('update:modelValue', newValue);
      },
    });

    const changed = () => {
      emit('change');
    };

    const { t } = useLabel();
    const { rules } = useValidationRules(t);

    const effectiveDeliveryMethods = computed(() => {
      const methods = Array.isArray(componentProps.deliveryMethods)
        ? componentProps.deliveryMethods
        : [];
      const enriched = methods.map(
        (method: DeliveryMethod) =>
          ({
            ...method,
            title: t(method.nameTranslationKey),
          }) as DeliveryMethod & { title: string }
      );

      return enriched;
    });

    return {
      t,
      deliveryMethod,
      changed,
      effectiveDeliveryMethods,
      rules,
    };
  },
});
</script>

<style scoped>
.compact-select :deep(.v-field__input) {
  padding-top: 8px;
  padding-bottom: 8px;
  min-height: 40px;
  font-family: "lato", sans-serif;
  font-weight: 400;
}

.compact-select :deep(.v-field) {
  min-height: 40px;
}
</style>
